import { ExclamationCircleOutlined, InboxOutlined, CalendarOutlined, TeamOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Tooltip } from 'antd';
import { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskDatePicker from './TaskDatePicker';

interface SplitOrderPublishConfirmModalProps {
    visible: boolean;
    onCancel: () => void;
    /**
     * 点「确认」：回传所选派送日期，由调用方按最新仓库时间复核并继续发布流程。
     * 返回 false 表示所选日期已不可选，弹窗清空日期并提示重新选择。
     */
    onConfirm: (taskDate: Dayjs) => Promise<boolean>;
    /** 仓库当前时间，决定日期面板中哪些日期可选 */
    warehouseNow: Dayjs;
    tableData: any[];
    selectedRowKeys: React.Key[];
}

const SplitOrderPublishConfirmModal: React.FC<SplitOrderPublishConfirmModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    warehouseNow,
    tableData,
    selectedRowKeys,
}) => {
    const { t } = useTranslation();
    const [taskDate, setTaskDate] = useState<Dayjs | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    // 每次打开都重新留空，必须由用户主动选择
    useEffect(() => {
        if (visible) {
            setTaskDate(null);
            setDateError(null);
            setConfirming(false);
        }
    }, [visible]);

    const handleConfirm = async () => {
        if (!taskDate) return;
        setConfirming(true);
        try {
            const accepted = await onConfirm(taskDate);
            if (!accepted) {
                setTaskDate(null);
                setDateError(t('delivery.routes.message.deliveryDateChanged'));
            }
        } finally {
            setConfirming(false);
        }
    };

    const publishStats = useMemo(() => {
        const selectedRows = tableData.filter(row => selectedRowKeys.includes(row.key));

        const totalParcels = selectedRows.reduce((sum, row) => sum + (row.totalParcels || 0), 0);

        // 按大区分组统计（首字母）
        const regionMap: Record<string, number> = {};
        selectedRows.forEach(row => {
            if (row.areaName) {
                const regionPrefix = row.areaName.charAt(0).toUpperCase();
                if (!regionMap[regionPrefix]) regionMap[regionPrefix] = 0;
                regionMap[regionPrefix] += row.totalParcels || 0;
            }
        });

        const regionStats = Object.entries(regionMap)
            .map(([region, count]) => ({ region, count }))
            .sort((a, b) => a.region.localeCompare(b.region));

        // 统计司机人数（去重）
        const driverIds = new Set<number>();
        selectedRows.forEach(row => {
            // 从 tableData 中找已合并 subRowDrivers 的子行
            const childSegments = tableData.filter(
                (r: any) => r.isSegment && r.parentRecord?.key === row.key
            );
            if (childSegments.length > 0) {
                childSegments.forEach((seg: any) => {
                    const id = Number(seg.driverAccountId || seg.driverId);
                    if (id) driverIds.add(id);
                });
            } else {
                const driverId = Number(row.driverId);
                if (driverId) driverIds.add(driverId);
            }
        });

        return { totalParcels, regionStats, driverCount: driverIds.size };
    }, [tableData, selectedRowKeys]);

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onCancel}
            width={500}
            footer={null}
            closable={false}
            destroyOnClose
        >
            <div className="p-4">
                <div className="flex items-center gap-3 mb-8">
                    <ExclamationCircleOutlined className="text-3xl text-red-500" />
                    <span className="text-2xl font-bold text-black">
                        {t('delivery.routes.message.publishConfirmTitle')}
                    </span>
                </div>

                <div className="mb-4 pl-6">
                    <div className="flex items-center mb-3">
                        <InboxOutlined className="text-xl mr-2 text-gray-500" />
                        <span className="text-lg text-black font-medium">
                            {t('delivery.routes.message.selectedParcels')}: {publishStats.totalParcels}
                        </span>
                    </div>

                    {publishStats.regionStats.length > 0 && (
                        <div className="pl-8">
                            {publishStats.regionStats.map(({ region, count }) => (
                                <div key={region} className="mb-1">
                                    <span className="text-lg text-gray-700">
                                        • {t('delivery.routes.message.region', { region })}: {t('delivery.routes.message.regionParcels', { count })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mb-4 pl-6">
                    {/* 标签过长（英 / 西）时日期框自动换行并与标签左对齐，避免撑出弹窗 */}
                    <div className="relative pl-8 mb-2">
                        <CalendarOutlined className="absolute left-0 top-1.5 text-xl text-gray-500" />
                        <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
                            <span className="text-lg font-medium mt-0.5">
                                {t('delivery.routes.message.selectedDeliveryDate')}
                                <Tooltip title={<div className="whitespace-pre-line">{t('delivery.routes.message.taskDateRuleTooltip')}</div>}>
                                    <QuestionCircleOutlined className="cursor-help ml-1 text-base" style={{ color: '#7c3aed' }} />
                                </Tooltip>
                                :
                            </span>
                            <TaskDatePicker
                                value={taskDate}
                                onChange={(date) => { setTaskDate(date); setDateError(null); }}
                                warehouseNow={warehouseNow}
                                placeholder={t('delivery.routes.message.selectDeliveryDatePlaceholder')}
                                errorText={dateError}
                            />
                        </div>
                    </div>
                    <div className="pl-8">
                        <span className="text-sm text-red-500">
                            {t('delivery.routes.message.publishErrorWarning')}
                        </span>
                    </div>
                </div>

                <div className="flex items-center mb-6 pl-6">
                    <TeamOutlined className="text-xl mr-2 text-gray-500" />
                    <span className="text-lg text-black font-medium">
                        {t('delivery.routes.message.assignedDriverCount')}: {publishStats.driverCount}
                    </span>
                </div>

                <div className="flex justify-end gap-3">
                    <Button size="large" onClick={onCancel} className="min-w-[120px]">
                        {t('delivery.routes.message.returnToModify')}
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleConfirm}
                        disabled={!taskDate}
                        loading={confirming}
                        className="min-w-[120px]"
                        style={taskDate ? { background: '#7c3aed' } : undefined}
                    >
                        {t('delivery.routes.message.confirmPublish')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SplitOrderPublishConfirmModal;